import cabezaUrl from "../assets/robot-head.png";

/**
 * LA BANDA. El elemento del que cuelga toda la interfaz.
 *
 * Una posicion de liquidez concentrada ES esto: un tramo de precio con el precio dentro. Enseñarlo
 * es mas honesto que contarlo con un parrafo, y resuelve de paso la pregunta que el usuario se hace
 * de verdad —¿mi dinero esta trabajando ahora mismo?— sin que tenga que interpretar dos numeros.
 *
 * Las marcas de abajo son los TICKS REALES del pool (su `tickSpacing`): la granularidad con la que
 * se puede colocar un borde. No son decoracion — cuando alguien elige un rango muy estrecho ve que
 * cabe en cuatro marcas, y entiende por que se sale tan a menudo.
 */

type Props = {
  /** precio actual */
  precio: number;
  /** borde inferior y superior del rango, en precio */
  lo: number;
  hi: number;
  /** separacion entre ticks, en precio, para las marcas de sonda. 0 = sin marcas */
  paso?: number;
  /** etiquetas de los bordes */
  etiquetas?: boolean;
  alto?: number;
  /** el precio se sale del rango: se dibuja fuera, no se recorta */
  compacta?: boolean;
  /**
   * En que se convierte la posicion si el precio llega a cada borde, ya escrito. Va debajo de cada
   * precio porque es la consecuencia de ese precio, no un dato aparte: en el borde la posicion
   * queda entera en una moneda, y eso es lo que sorprende a quien no ha visto un rango antes.
   */
  extremos?: { izq: string; der: string; nota?: string } | null;
  /** el precio se salió y el keeper va a re-centrar: el robot de Keel CAMINA por la barra real,
   *  de un borde del rango al otro, para que se vea que está reajustando. */
  reajustando?: boolean;
};

const BORDE = "si el precio llega a este borde";

export function Banda({
  precio, lo, hi, paso = 0, etiquetas = true, alto = 56, compacta, extremos, reajustando,
}: Props) {
  // La vista siempre muestra el rango MAS el precio, con margen: si el precio se escapo, tiene que
  // verse fuera de la banda. Recortarlo seria esconder justo lo que hay que mirar.
  const min = Math.min(lo, precio);
  const max = Math.max(hi, precio);
  const margen = (max - min) * 0.18 || max * 0.02;
  const v0 = min - margen;
  const v1 = max + margen;
  const pos = (x: number) => ((x - v0) / (v1 - v0)) * 100;

  const dentro = precio >= lo && precio < hi;
  const marcas: number[] = [];
  if (paso > 0) {
    const n = Math.floor((v1 - v0) / paso);
    if (n > 0 && n < 90) for (let x = Math.ceil(v0 / paso) * paso; x < v1; x += paso) marcas.push(x);
  }

  return (
    <figure className={`banda ${compacta ? "compacta" : ""}`} style={{ ["--alto" as string]: `${alto}px` }}>
      <div className="carril">
        <div className="tramo" style={{ left: `${pos(lo)}%`, width: `${pos(hi) - pos(lo)}%` }} />
        <div className={`aguja ${dentro ? "" : "fuera"}`} style={{ left: `${pos(precio)}%` }}>
          <span className="cifra">{fmt(precio)}</span>
        </div>
        {/* FUERA DE RANGO: el robot de Keel camina por la barra REAL, de un borde del rango al otro,
            recolocando los precios. Las posiciones son las de los bordes reales (pos(lo)/pos(hi)). */}
        {reajustando && (
          <div
            className="robot-paseo"
            aria-hidden
            style={{ ["--r0" as string]: `${pos(lo)}%`, ["--r1" as string]: `${pos(hi)}%` }}
          >
            <img className="robot-paseo-cara" src={cabezaUrl} alt="" width={26} draggable={false} />
            <svg className="robot-paseo-cuerpo" viewBox="0 0 40 40" fill="none" aria-hidden focusable="false">
              <rect x="10" y="2" width="20" height="20" rx="7" fill="var(--casco-alto)" stroke="var(--baliza)" strokeWidth="1.6" />
              <circle className="robot-pecho" cx="20" cy="12" r="2.6" fill="var(--baliza)" />
              <rect className="robot-brazo-izq" x="4" y="5" width="5" height="13" rx="2.5" fill="var(--casco-alto)" stroke="var(--baliza)" strokeWidth="1.4" />
              <rect className="robot-brazo-der" x="31" y="5" width="5" height="13" rx="2.5" fill="var(--casco-alto)" stroke="var(--baliza)" strokeWidth="1.4" />
              <rect className="robot-pierna robot-pierna-a" x="13" y="22" width="6" height="14" rx="3" fill="var(--casco-alto)" stroke="var(--baliza)" strokeWidth="1.4" />
              <rect className="robot-pierna robot-pierna-b" x="21" y="22" width="6" height="14" rx="3" fill="var(--casco-alto)" stroke="var(--baliza)" strokeWidth="1.4" />
            </svg>
          </div>
        )}
      </div>

      {marcas.length > 0 && (
        <div className="sondas" aria-hidden>
          {marcas.map((m) => (
            <i key={m} style={{ left: `${pos(m)}%` }} />
          ))}
        </div>
      )}

      {etiquetas && (
        <figcaption>
          <span className="borde">
            <span className="p">{fmt(lo)}</span>
            {extremos && <span className="q" title={extremos.nota ?? BORDE}>{extremos.izq}</span>}
          </span>
          <span className={dentro ? "estado" : "estado fuera"}>
            <span className="p">
              {dentro ? "en rango" : precio < lo ? "salió por abajo" : "salió por arriba"}
            </span>
            {/* LO ANCHO QUE ES EL RANGO, de borde a borde: la AMPLITUD total. Es el número que
                decide cuánto se reajusta el robot y cuánto gas gasta, y el que el dueño reconoce de
                su gráfico. Antes se mostraba el semiancho (±%); ahora la amplitud completa. */}
            <span className="amplitud" title="lo ancho que es el rango, de borde a borde">
              amplitud {fmtPct(amplitud(lo, hi))}
            </span>
          </span>
          <span className="borde der">
            <span className="p">{fmt(hi)}</span>
            {extremos && <span className="q" title={extremos.nota ?? BORDE}>{extremos.der}</span>}
          </span>
        </figcaption>
      )}
    </figure>
  );
}

function fmt(n: number) {
  if (!Number.isFinite(n)) return "—";
  const a = Math.abs(n);
  return n.toLocaleString("es", { maximumFractionDigits: a < 1 ? 6 : a < 100 ? 3 : 2 });
}

/**
 * La AMPLITUD del rango, en %: cuánto está el borde alto por encima del bajo, (hi/lo − 1) × 100. Es
 * el ancho TOTAL de borde a borde —lo que el precio puede recorrer de un extremo al otro—, no el
 * semiancho (±%) con el que se teclea al crear. Para un rango 78,98–91,76 son 16,2%. Se muestra así
 * a petición del dueño, que lo compara con la amplitud que ve en su gráfico.
 */
const amplitud = (lo: number, hi: number) =>
  lo > 0 && hi > lo ? (hi / lo - 1) * 100 : 0;

const fmtPct = (p: number) =>
  p.toLocaleString("es", { maximumFractionDigits: p < 1 ? 2 : 1, minimumFractionDigits: p < 1 ? 2 : 1 }) + "%";

/**
 * La banda del heroe: el precio deriva, se sale, y el rango se re-centra. Es el producto entero en
 * seis segundos y sin una sola palabra. Se para sola si el visitante pidio menos movimiento.
 */
export function BandaViva() {
  return (
    <div className="banda-viva">
      <div className="carril" aria-hidden>
        <div className="tramo" />
        <div className="aguja" />
      </div>
      <div className="sondas" aria-hidden>
        {Array.from({ length: 41 }, (_, i) => (
          <i key={i} style={{ left: `${i * 2.5}%` }} />
        ))}
      </div>
      <div className="leyenda">
        <span className="l-tramo">tu rango</span>
        <span className="l-fuera">fuera no cobras</span>
      </div>
      <p className="sr">
        El precio deriva, se sale del rango por abajo y el rango se estira hacia arriba para
        recuperar el capital antes de volver a centrarse.
      </p>
    </div>
  );
}
